import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  Package,
  PackageX,
  RotateCw,
} from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import EmptyState from '../../components/site/EmptyState';
import SEO from '../../components/SEO';
import paymentService from '../../services/paymentService';
import accountService from '../../services/accountService';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import { formatINR } from '../../utils/format';
import { getErrorMessage } from '../../utils/apiError';
import { contactInfo, getHoursSentence } from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import './checkout.css';

const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const productImage = product =>
  product?.media?.find(m => !m.type || m.type === 'image')?.url || null;

const isNonZero = value => Number(value) > 0;

const CheckoutSkeleton = () => (
  <div className="pk-split" aria-hidden="true">
    <div className="pk-stack">
      <div className="pk-skel co-skel co-skel--product" />
      <div className="pk-skel co-skel co-skel--form" />
      <div className="pk-skel co-skel co-skel--small" />
    </div>
    <div className="pk-skel co-skel co-skel--summary" />
  </div>
);

const CheckoutPage = () => {
  useSiteConfig();
  const { productId } = useParams();
  const navigate = useNavigate();

  // Everything checkout needs from the server, loaded together.
  const [load, setLoad] = useState({ status: 'loading' });

  // Form
  const [buyerStateCode, setBuyerStateCode] = useState('');
  const [showGst, setShowGst] = useState(false);
  const [buyerGstin, setBuyerGstin] = useState('');
  const [buyerBusinessName, setBuyerBusinessName] = useState('');
  const [method, setMethod] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const stateRef = useRef(null);
  const gstinRef = useRef(null);
  const termsRef = useRef(null);

  const loadCheckout = useCallback(async () => {
    setLoad({ status: 'loading' });
    const [settings, methods, quote, product, me] = await Promise.allSettled([
      paymentService.getCheckoutSettings(),
      paymentService.getPaymentMethods(),
      paymentService.getQuote(productId),
      paymentService.getProduct(productId),
      accountService.getMe(),
    ]);

    // The quote decides whether this product can be bought at all (inactive, hidden, not found...).
    if (quote.status === 'rejected') {
      const status = quote.reason?.response?.status;
      setLoad({
        status: status === 400 || status === 404 ? 'unavailable' : 'error',
        message: getErrorMessage(quote.reason, "We couldn't load the price for this project."),
      });
      return;
    }
    const failed = [settings, methods].find(r => r.status === 'rejected');
    if (failed) {
      setLoad({
        status: 'error',
        message: getErrorMessage(failed.reason, "We couldn't load checkout."),
      });
      return;
    }

    const enabledMethods = methods.value || [];
    setMethod(current =>
      enabledMethods.some(m => m.key === current)
        ? current
        : (enabledMethods.find(m => m.isDefault) || enabledMethods[0])?.key || ''
    );
    setLoad({
      status: 'ready',
      settings: settings.value || {},
      methods: enabledMethods,
      quote: quote.value,
      // The product is only for the summary picture and edition; the quote has the name.
      product: product.status === 'fulfilled' ? product.value : null,
      // Only used for the "verify your email" prompt; checkout works without it.
      user: me.status === 'fulfilled' && me.value?.success ? me.value.data : null,
    });
  }, [productId]);

  useEffect(() => {
    loadCheckout();
  }, [loadCheckout]);

  const backTo = `/products/${encodeURIComponent(productId)}`;

  const header = (
    <>
      <SEO title="Checkout" noindex />
      <Link to={backTo} className="co-back">
        <ArrowLeft aria-hidden="true" />
        Back to project
      </Link>
      <PageHeader title="Checkout" />
    </>
  );

  if (load.status === 'loading') {
    return (
      <main className="pk-page co-page">
        <div className="pk-container">
          {header}
          <p className="co-sr-only" role="status">
            Loading checkout
          </p>
          <CheckoutSkeleton />
        </div>
      </main>
    );
  }

  if (load.status === 'unavailable' || load.status === 'error') {
    const unavailable = load.status === 'unavailable';
    return (
      <main className="pk-page co-page">
        <div className="pk-container">
          {header}
          <EmptyState
            role="alert"
            icon={unavailable ? <PackageX /> : <AlertCircle />}
            title={unavailable ? "This project can't be bought right now" : "We couldn't load checkout"}
            text={load.message}
            actions={
              unavailable ? (
                <Link to="/products" className="pk-btn pk-btn--primary pk-btn--sm">
                  Browse projects
                </Link>
              ) : (
                <>
                  <button
                    type="button"
                    className="pk-btn pk-btn--primary pk-btn--sm"
                    onClick={loadCheckout}
                  >
                    <RotateCw aria-hidden="true" />
                    Retry
                  </button>
                  <Link to={backTo} className="pk-btn pk-btn--secondary pk-btn--sm">
                    Back to project
                  </Link>
                </>
              )
            }
          />
        </div>
      </main>
    );
  }

  const { settings, methods, quote, product } = load;
  const states = settings.states || [];
  const requireState = settings.requireBuyerState ?? true;
  const requireTerms = settings.requireTermsAcceptance ?? true;
  // Only when the store requires it; the server enforces it too.
  const emailBlocked = Boolean(settings.requireVerifiedEmail && load.user && !(load.user.email && load.user.emailConfirmed));
  const selectedMethod = methods.find(m => m.key === method) || null;
  const image = productImage(product);
  const name = product?.name || quote.productName;
  const edition = product?.digitalProductDetails?.edition;
  const discount = Number(quote.discountAmount ?? quote.discount ?? 0);
  const taxLabel = quote.taxLabel || 'GST';

  const validate = () => {
    const problems = {};
    if (requireState && !buyerStateCode) {
      problems.state = 'Select your state. It is the place of supply on your GST invoice.';
    }
    if (showGst && buyerGstin.trim()) {
      const gstin = buyerGstin.trim().toUpperCase();
      if (!GSTIN_PATTERN.test(gstin)) {
        problems.gstin = 'Enter a valid 15-character GSTIN, for example 29ABCDE1234F1Z5.';
      } else if (buyerStateCode && gstin.slice(0, 2) !== buyerStateCode) {
        const gstinState = states.find(s => s.code === gstin.slice(0, 2));
        problems.gstin = gstinState
          ? `This GSTIN is registered in ${gstinState.name}. Choose that state or remove the GSTIN.`
          : 'The GSTIN does not match the selected state.';
      }
    }
    if (requireTerms && !acceptedTerms) {
      problems.terms = 'Please accept the Terms to continue.';
    }
    return problems;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting || !selectedMethod) return;
    if (emailBlocked) {
      setSubmitError('Please verify your email address first. We send your access and invoice there.');
      return;
    }

    const problems = validate();
    setErrors(problems);
    setSubmitError('');
    if (problems.state) {
      stateRef.current?.focus();
      return;
    }
    if (problems.gstin) {
      gstinRef.current?.focus();
      return;
    }
    if (problems.terms) {
      termsRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const gstin = showGst ? buyerGstin.trim().toUpperCase() : '';
      const result = await paymentService.createOrder({
        productId,
        paymentMethod: selectedMethod.key,
        orderNotes: orderNotes.trim(),
        acceptedTerms,
        buyerStateCode,
        buyerGstin: gstin,
        buyerBusinessName: showGst ? buyerBusinessName.trim() : '',
      });

      const orderId = result?.orderId;
      if (!orderId) throw new Error('missing order id');

      // Hosted checkout (e.g. PhonePe): same-tab redirect; the gateway returns to /payment-status.
      if (result.paymentFlow === 'Redirect' && result.paymentRedirectUrl) {
        window.location.assign(result.paymentRedirectUrl);
        return;
      }

      // Replace, so Back from the payment page doesn't land on a form that places the order again.
      navigate(`/checkout/pay/${encodeURIComponent(orderId)}`, { replace: true });
    } catch (error) {
      setSubmitError(
        getErrorMessage(error, "We couldn't create your order. Please try again.")
      );
      setSubmitting(false);
    }
  };

  return (
    <main className="pk-page co-page">
      <div className="pk-container">
        {header}

        <div className="pk-split">
          <form
            id="checkout-form"
            className="pk-stack co-main"
            onSubmit={handleSubmit}
            noValidate
            aria-label="Checkout"
          >
            {/* Product */}
            <section className="pk-card co-product" aria-label="Project">
              <div className="co-product__media">
                {image ? (
                  <img src={image} alt="" loading="lazy" />
                ) : (
                  <Package aria-hidden="true" />
                )}
              </div>
              <div className="co-product__body">
                <h2 className="pk-title-3 co-product__name">{name}</h2>
                {edition && <p className="pk-muted co-product__meta">{edition} edition</p>}
                <p className="co-product__price">{formatINR(quote.total)}</p>
              </div>
            </section>

            {/* Buyer details */}
            <section className="pk-card" aria-labelledby="co-details-title">
              <h2 className="pk-card__title co-card-title" id="co-details-title">
                Your details
              </h2>
              <div className="pk-form">
                <div className="pk-field">
                  <label className="pk-label" htmlFor="co-state">
                    State{!requireState && <span className="pk-optional"> (optional)</span>}
                  </label>
                  <select
                    id="co-state"
                    ref={stateRef}
                    className="pk-select"
                    value={buyerStateCode}
                    required={requireState}
                    aria-invalid={errors.state ? 'true' : undefined}
                    aria-describedby={errors.state ? 'co-state-error' : 'co-state-hint'}
                    onChange={e => {
                      setBuyerStateCode(e.target.value);
                      if (errors.state) setErrors(prev => ({ ...prev, state: undefined }));
                    }}
                  >
                    <option value="">Select your state</option>
                    {states.map(s => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  {errors.state ? (
                    <p className="pk-error" id="co-state-error">
                      {errors.state}
                    </p>
                  ) : (
                    <p className="pk-hint" id="co-state-hint">
                      Shown as the place of supply on your GST invoice.
                    </p>
                  )}
                </div>

                <div className="co-disclosure">
                  <button
                    type="button"
                    className="co-disclosure__toggle"
                    aria-expanded={showGst}
                    aria-controls="co-gst-fields"
                    onClick={() => setShowGst(open => !open)}
                  >
                    Add GST details
                    <span className="pk-optional"> (optional)</span>
                    <ChevronDown aria-hidden="true" />
                  </button>
                  <div id="co-gst-fields" className="co-disclosure__panel" hidden={!showGst}>
                    <p className="pk-hint">
                      For a business invoice with your GSTIN. The state above must match the GSTIN.
                    </p>
                    <div className="pk-form__row">
                      <div className="pk-field">
                        <label className="pk-label" htmlFor="co-gstin">
                          GSTIN
                        </label>
                        <input
                          id="co-gstin"
                          ref={gstinRef}
                          className="pk-input co-mono"
                          value={buyerGstin}
                          maxLength={15}
                          autoComplete="off"
                          spellCheck={false}
                          placeholder="29ABCDE1234F1Z5"
                          aria-invalid={errors.gstin ? 'true' : undefined}
                          aria-describedby={errors.gstin ? 'co-gstin-error' : undefined}
                          onChange={e => {
                            setBuyerGstin(e.target.value.toUpperCase().replace(/\s/g, ''));
                            if (errors.gstin) setErrors(prev => ({ ...prev, gstin: undefined }));
                          }}
                        />
                        {errors.gstin && (
                          <p className="pk-error" id="co-gstin-error">
                            {errors.gstin}
                          </p>
                        )}
                      </div>
                      <div className="pk-field">
                        <label className="pk-label" htmlFor="co-business">
                          Business name
                        </label>
                        <input
                          id="co-business"
                          className="pk-input"
                          value={buyerBusinessName}
                          maxLength={100}
                          autoComplete="organization"
                          onChange={e => setBuyerBusinessName(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Payment method */}
            <section className="pk-card" aria-labelledby="co-method-title">
              <h2 className="pk-card__title co-card-title" id="co-method-title">
                Payment method
              </h2>
              {methods.length === 0 && (
                <div className="pk-alert" data-tone="warning" role="alert">
                  <AlertCircle aria-hidden="true" />
                  <span>
                    Online payment isn't available right now. Message us on WhatsApp at{' '}
                    {contactInfo.whatsapp.display} ({getHoursSentence()}) to
                    buy this project.
                  </span>
                </div>
              )}
              {methods.length === 1 && (
                <div className="co-method co-method--static">
                  <span className="co-method__name">{methods[0].displayName}</span>
                  <span className="pk-hint">{methodHint(methods[0])}</span>
                </div>
              )}
              {methods.length > 1 && (
                <fieldset className="co-methods">
                  <legend className="co-sr-only">Choose how to pay</legend>
                  {methods.map(m => (
                    <label
                      key={m.key}
                      className={`co-method${method === m.key ? ' is-active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={m.key}
                        checked={method === m.key}
                        onChange={() => setMethod(m.key)}
                      />
                      <span className="co-method__text">
                        <span className="co-method__name">{m.displayName}</span>
                        <span className="pk-hint">{methodHint(m)}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
              )}
            </section>

            {/* Notes */}
            <section className="pk-card" aria-label="Order notes">
              <div className="pk-field">
                <label className="pk-label" htmlFor="co-notes">
                  Order notes <span className="pk-optional">(optional)</span>
                </label>
                <textarea
                  id="co-notes"
                  className="pk-textarea co-notes"
                  value={orderNotes}
                  maxLength={1000}
                  onChange={e => setOrderNotes(e.target.value)}
                />
              </div>
            </section>

            {/* Terms */}
            <div className="co-terms">
              {quote.licenseTerms && (
                <details className="co-licence">
                  <summary>Licence for this project</summary>
                  <p>{quote.licenseTerms}</p>
                </details>
              )}
              <label className="pk-check" htmlFor="co-terms">
                <input
                  id="co-terms"
                  ref={termsRef}
                  type="checkbox"
                  checked={acceptedTerms}
                  aria-invalid={errors.terms ? 'true' : undefined}
                  aria-describedby={errors.terms ? 'co-terms-error' : undefined}
                  onChange={e => {
                    setAcceptedTerms(e.target.checked);
                    if (e.target.checked && errors.terms) {
                      setErrors(prev => ({ ...prev, terms: undefined }));
                    }
                  }}
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms" className="pk-link" target="_blank" rel="noopener noreferrer">
                    Terms
                  </Link>{' '}
                  and understand all sales are final (
                  <Link to="/refund" className="pk-link" target="_blank" rel="noopener noreferrer">
                    refund policy
                  </Link>
                  ).
                </span>
              </label>
              {errors.terms && (
                <p className="pk-error" id="co-terms-error">
                  {errors.terms}
                </p>
              )}
            </div>
          </form>

          {/* Summary */}
          <aside className="pk-sticky" aria-labelledby="co-summary-title">
            <div className="pk-card co-summary">
              <h2 className="pk-card__title co-card-title" id="co-summary-title">
                Order summary
              </h2>
              <dl className="pk-rows">
                <div>
                  <dt>Subtotal</dt>
                  <dd>{formatINR(quote.unitPrice)}</dd>
                </div>
                {isNonZero(quote.platformFee) && (
                  <div>
                    <dt>Platform fee</dt>
                    <dd>{formatINR(quote.platformFee)}</dd>
                  </div>
                )}
                {discount > 0 && (
                  <div>
                    <dt>Discount</dt>
                    <dd>-{formatINR(discount)}</dd>
                  </div>
                )}
                {isNonZero(quote.taxAmount) && !quote.pricesIncludeTax && (
                  <div>
                    <dt>
                      {taxLabel}
                      {isNonZero(quote.taxRatePercent) && ` (${quote.taxRatePercent}%)`}
                    </dt>
                    <dd>{formatINR(quote.taxAmount)}</dd>
                  </div>
                )}
                <div className="pk-rows__total">
                  <dt>Total</dt>
                  <dd>{formatINR(quote.total)}</dd>
                </div>
              </dl>
              {isNonZero(quote.taxAmount) && quote.pricesIncludeTax && (
                <p className="pk-hint co-summary__note">
                  Includes {formatINR(quote.taxAmount)} {taxLabel}
                </p>
              )}

              {load.user && (
                <EmailVerificationBanner
                  user={load.user}
                  required={emailBlocked}
                  className="co-summary__alert"
                  onChangeEmail={() => navigate('/profile?tab=account')}
                />
              )}

              {submitError && (
                <div className="pk-alert co-summary__alert" data-tone="danger" role="alert">
                  <AlertCircle aria-hidden="true" />
                  <span>{submitError}</span>
                </div>
              )}

              <button
                type="submit"
                form="checkout-form"
                className="pk-btn pk-btn--primary pk-btn--block co-summary__cta"
                disabled={!selectedMethod || emailBlocked}
                aria-busy={submitting ? 'true' : undefined}
              >
                Continue to payment
              </button>
              {selectedMethod && (
                <p className="pk-hint co-summary__note">
                  {selectedMethod.flow === 'Redirect'
                    ? `You'll pay on the ${selectedMethod.displayName} page.`
                    : 'Next, pay by UPI. We verify each payment by hand, typically within 12 hours.'}
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

function methodHint(method) {
  return method.flow === 'Redirect'
    ? "You'll be taken to the payment page to pay."
    : 'Scan a QR or open your UPI app. Verified by hand, typically within 12 hours.';
}

export default CheckoutPage;
